import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { removeTokenCookie } from '@root/utils/cookies/cookies';

import roleService from '../services/roleService';
import superOrganizationService from '../services/superOrganizationService';

export const signOut = createAction('signOutGlobally');

export const listAllRolesForSuperOrganization = createAsyncThunk(
  'roles/listAllRolesForSuperOrganization',
  async ({ superOrganizationId }) => {
    return roleService.listAllRolesForSuperOrganization(superOrganizationId);
  }
);

export const listAllRolesForSuperOrganizationWithFullInfo = createAsyncThunk(
  'roles/listAllRolesForSuperOrganizationWithFullInfo',
  async ({ superOrganizationId }) => {
    return roleService.listAllRolesForSuperOrganizationWithFullInfo(superOrganizationId);
  }
);

export const getRole = createAsyncThunk('roles/getRole', async ({ roleId, superOrganizationId }) => {
  return roleService.getRole(superOrganizationId, roleId);
});

export const addUsersToRole = createAsyncThunk('roles/addUsersToRole', async ({ superOrganizationId, roleId, userArray }) => {
  return roleService.addUsersToRole(superOrganizationId, roleId, userArray);
});

export const removeUsersFromRole = createAsyncThunk('roles/removeUsersFromRole', async ({ superOrganizationId, roleId, userArray }) => {
  return roleService.removeUsersFromRoleWithId(superOrganizationId, roleId, userArray);
});

export const listSuperOrganizationUsers = createAsyncThunk(
  'roles/listSuperOrganizationUsers',
  async ({ superOrganizationId, roleToExcludeId }) => {
    return superOrganizationService.listSuperOrganizationUsers(superOrganizationId, roleToExcludeId);
  }
);

const INITIAL_STATE = {
  roles: [],
  isLoading: false,
  isError: false,
  errorMessage: null,
  isErrorUpdating: false,
  isUpdatingRole: false,
  permissions: [],
  isLoadingPermissions: false,
  superOrgUsers: [],
  errorFetchingSuperOrgUsers: false,
  loadingSuperOrgUsers: false
};

export const roleSlice = createSlice({
  name: 'roles',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listAllRolesForSuperOrganization.fulfilled, (state, action) => {
        state.roles = action.payload;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listAllRolesForSuperOrganization.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listAllRolesForSuperOrganization.rejected, (state, action) => {
        state.roles = [];
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(listAllRolesForSuperOrganizationWithFullInfo.fulfilled, (state, action) => {
        state.roles = action.payload;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listAllRolesForSuperOrganizationWithFullInfo.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listAllRolesForSuperOrganizationWithFullInfo.rejected, (state, action) => {
        state.roles = [];
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getRole.fulfilled, (state, action) => {
        const newRole = action.payload;
        const roles = [...(state.roles || [])];
        const indexOfNewRoleInCurrentRoles = roles.findIndex((role) => role.id === newRole.id);

        if (indexOfNewRoleInCurrentRoles !== -1) {
          roles[indexOfNewRoleInCurrentRoles] = newRole;
        } else {
          roles.push(newRole);
        }
        state.roles = roles;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getRole.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getRole.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(addUsersToRole.fulfilled, (state, action) => {
        const updatedRole = action.payload;

        state.roles = state.roles.map((item) => (item.id === updatedRole.id ? updatedRole : item));
        state.isUpdatingRole = false;
        state.isErrorUpdating = false;
        state.errorMessage = null;
      })
      .addCase(addUsersToRole.pending, (state) => {
        state.isUpdatingRole = true;
        state.isErrorUpdating = false;
        state.errorMessage = null;
      })
      .addCase(addUsersToRole.rejected, (state, action) => {
        state.isUpdatingRole = false;
        state.isErrorUpdating = true;
        state.errorMessage = action.payload;
      })
      .addCase(removeUsersFromRole.fulfilled, (state, action) => {
        const updatedRole = action.payload;

        state.roles = state.roles.map((item) => (item.id === updatedRole.id ? updatedRole : item));
        state.isUpdatingRole = false;
        state.isErrorUpdating = false;
        state.errorMessage = null;
      })
      .addCase(removeUsersFromRole.pending, (state) => {
        state.isUpdatingRole = true;
        state.isErrorUpdating = false;
        state.errorMessage = null;
      })
      .addCase(removeUsersFromRole.rejected, (state, action) => {
        state.isUpdatingRole = false;
        state.isErrorUpdating = true;
        state.errorMessage = action.payload;
      })
      .addCase(listSuperOrganizationUsers.fulfilled, (state, action) => {
        state.superOrgUsers = action.payload;
        state.loadingSuperOrgUsers = false;
        state.errorFetchingSuperOrgUsers = false;
      })
      .addCase(listSuperOrganizationUsers.pending, (state) => {
        state.loadingSuperOrgUsers = true;
        state.errorFetchingSuperOrgUsers = false;
      })
      .addCase(listSuperOrganizationUsers.rejected, (state) => {
        state.roles = [];
        state.loadingSuperOrgUsers = false;
        state.errorFetchingSuperOrgUsers = true;
      })
      .addCase(signOut, (state) => {
        removeTokenCookie();
        localStorage.removeItem('token');
        return { ...INITIAL_STATE };
      });
  }
});

export const selectSuperOrganizationUsers = createSelector(
  [(state) => state.roles.superOrgUsers, (state) => state.roles.loadingSuperOrgUsers, (state) => state.roles.errorFetchingSuperOrgUsers],
  (superOrgUsers, loadingSuperOrgUsers, errorFetchingSuperOrgUsers) => ({ superOrgUsers, loadingSuperOrgUsers, errorFetchingSuperOrgUsers })
);

export const selectActiveRole = (selectedRoleId) =>
  createSelector(
    [
      (state) => state.roles.roles,
      (state) => state.roles.isError,
      (state) => state.roles.isLoading,
      (state) => state.roles.errorMessage,
      (state) => state.roles.isErrorUpdating,
      (state) => state.roles.isUpdatingRole
    ],
    (roles, isError, isLoading, errorMessage, isErrorUpdating, isUpdatingRole) => {
      const selectedRole = selectedRoleId ? (roles || []).find((role) => role.id === selectedRoleId) : undefined;

      return { roles, selectedRole, isError, isLoading, errorMessage, isErrorUpdating, isUpdatingRole };
    }
  );

export default roleSlice.reducer;
